/* eslint-disable react/style-prop-object */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import {ArrowDropDown, Clear} from '@material-ui/icons';
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl';
import {useHistory, useParams} from 'react-router';
import cn from 'classnames';
import {DeployStatus} from '../DeploymentStatus/DeploymentStatus';
import {DeploymentActionButton} from '../DeploymentActionButton/DeploymentActionButton';
import {Pagination} from '@material-ui/lab';
import {StudioKebabMenu} from '../../../base-components/StudioKebabMenu';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../../store';
import {
  DeploymentDashboardRequest,
  DeploymentSortableColumn,
} from '../../../types/deployment/DeploymentDashboardRequest';
import {
  deleteDeployment,
  fetchDeployments,
  fetchDeviceGroups,
  restartDeployment,
  stopDeployment,
} from '../../../store/prodDeployments/dashboard';
import {toast} from '../../../base-components/StudioToast';
import {DeploymentDashboardInfo} from '../../../types/deployment/DeploymentDashboardResponse';
import HumanReadableTime from '../../../base-components/StudioHumanReadableTime/HumanReadableTime';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {sourceToTextMap} from '..';
import {actions as retrainActions} from '../../../store/prodDeployments/retrain';
import {DeploymentStatusType} from '../../../types/deployment/DeploymentResponse';
import {isNumber} from 'lodash';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {DeviceStatus} from '../DeploymentStatus/DeviceStatus';
import {REQUEST_POLLING_INTERVAL} from '../../../config/constants';

const {setRetrainData} = retrainActions;

const PAGE_SIZE = 6;

type DeploymentsTableProps = {
  search: string;
};

const DeploymentsOptions: Record<string, string> = {
  all: 'All deployments',
  userOnly: 'My deployments',
  userRelatedToProject: 'My deployments related to project',
  deleted: 'Deleted deployments',
};

export const DeploymentsTable = ({search}: DeploymentsTableProps) => {
  const history = useHistory();
  const intl = useIntl();
  const {projectId: projectContext} = useParams<{projectId: string}>();
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState('all');
  const [deviceGroupFilter, setDeviceGroupFilter] = useState<string>('');
  const [sort, setSort] = useState<DeploymentSortableColumn>('creationTime');
  const [sortDir, setSortDir] = useState<-1 | 1>(-1);
  const [page, setPage] = useState(0);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    deployment: DeploymentDashboardInfo | null;
  }>({isOpen: false, deployment: null});

  const [expectedStatus, setExpectedStatus] = useState<Map<string, DeploymentStatusType>>(
    new Map()
  );
  const userId = useSelector((state: RootState) => state.user.userId);

  function startMonitoringStatus(id: string, status: DeploymentStatusType) {
    setExpectedStatus(new Map(expectedStatus).set(id, status));
  }

  function stopMonitoringStatus(id: string) {
    const map = new Map(expectedStatus);
    map.delete(id);
    setExpectedStatus(map);
  }

  useEffect(() => {
    setPage(0);
  }, [sort, sortDir, filter, deviceGroupFilter, search]);

  useEffect(() => {
    const requestBody = {
      firstPage: 0,
      paginated: false,
    };
    dispatch(fetchDeviceGroups(requestBody));
  }, [dispatch]);

  useEffect(() => {
    const requestBody: DeploymentDashboardRequest = {
      firstPage: page,
      pageSize: PAGE_SIZE,
      paginated: true,
      orderDefinitions: [{field: sort, direction: sortDir === -1 ? 'DESC' : 'ASC'}],
      params: {
        name: search,
        deploymentType: ['PRODUCTION'],
        status:
          filter === 'deleted'
            ? ['DELETED']
            : [
                'IDLE',
                'COPYING_DATA',
                'DEPLOYING',
                'STARTING',
                'RUNNING',
                'STOPPING',
                'ERROR',
              ],
        deviceGroupId: deviceGroupFilter.length ? deviceGroupFilter : undefined,
      },
      scope:
        filter === 'userRelatedToProject'
          ? 'PROJECT'
          : filter === 'userOnly'
          ? 'USER'
          : 'GROUP',
      projectId: filter === 'userRelatedToProject' ? projectContext : undefined,
    };

    let promise = dispatch(fetchDeployments(requestBody));
    const interval = setInterval(() => {
      promise = dispatch(fetchDeployments(requestBody));
    }, REQUEST_POLLING_INTERVAL);

    return () => {
      promise.abort();
      clearInterval(interval);
    };
  }, [search, sort, sortDir, page, filter, deviceGroupFilter, dispatch, projectContext]);

  const deployments = useSelector(
    (state: RootState) => state.deploymentDashboard.deployments.data
  );
  const totalResults = useSelector(
    (state: RootState) => state.deploymentDashboard.deployments.count
  );
  const optimisticallyUpdatedStatuses = useSelector(
    (state: RootState) => state.deploymentDashboard.optimisticallyUpdatedStatuses
  );
  const deviceGroups = useSelector(
    (state: RootState) => state.deploymentDashboard.deviceGroups.data
  );

  const paginationCount =
    Math.ceil(totalResults / PAGE_SIZE) === 0 ? 1 : Math.ceil(totalResults / PAGE_SIZE);
  const paginationPage = page + 1;

  for (const deployment of deployments) {
    if (expectedStatus.size === 0) {
      break;
    }
    const status = expectedStatus.get(deployment.id);
    if (status !== deployment.status) {
      continue;
    }
    if (status === 'RUNNING') {
      toast.success(`Successfully started deployment ${deployment.name}`);
    }
    if (status === 'IDLE') {
      toast.success(`Successfully stopped deployment ${deployment.name}`);
    }
    stopMonitoringStatus(deployment.id);
  }

  const handleSort = (column: DeploymentSortableColumn) => {
    if (column === sort) {
      setSortDir(sortDir === 1 ? -1 : 1);
    } else {
      setSort(column);
      setSortDir(1);
    }
  };

  const handleDeployAction = async (row: DeploymentDashboardInfo) => {
    /*TODO: refactor it. Method should not run 2 opposite actions. Method should not rely
       on the cached status: server may already change it by the time of request. */
    if (row.status === 'IDLE') {
      const result = await dispatch(restartDeployment(row.id));
      if (result.meta.requestStatus === 'fulfilled') {
        startMonitoringStatus(row.id, 'RUNNING');
      }
    } else if (row.status !== 'ERROR') {
      const result = await dispatch(stopDeployment(row.id));
      if (result.meta.requestStatus === 'fulfilled') {
        startMonitoringStatus(row.id, 'IDLE');
      }
    }
  };

  const handleDeleteDeployment = async ({name, id}: DeploymentDashboardInfo) => {
    await dispatch(deleteDeployment(id));
    if (expectedStatus.get(id)) {
      stopMonitoringStatus(id);
    }
    toast.success(`Successfully deleted deployment ${name}`);
  };

  const getSortDirection = (): 'asc' | 'desc' => {
    return sortDir === 1 ? 'asc' : 'desc';
  };

  const deviceGroupsOptions = useMemo(
    () =>
      deviceGroups.reduce<Record<string, string>>((options, newOption) => {
        options[newOption.id] = newOption.name;

        return options;
      }, {}),
    [deviceGroups]
  );

  const renderStartAdornment = useCallback(
    (val: string) => {
      const status = deviceGroups.find(d => d.id === val)?.status;
      return status && <DeviceStatus status={status} showLabel={false} />;
    },
    [deviceGroups]
  );

  const EndAdornment = useCallback(() => {
    return deviceGroupFilter.length > 0 ? (
      <InputAdornment className="prod-deploy-dashboard__select_clear_icon" position="end">
        <Clear onClick={() => setDeviceGroupFilter('')} />
      </InputAdornment>
    ) : null;
  }, [deviceGroupFilter]);

  const deploymentOptions = useMemo(() => {
    if (projectContext) {
      return DeploymentsOptions;
    } else {
      const {userRelatedToProject: _, ...deploymentOptions} = DeploymentsOptions;
      return deploymentOptions;
    }
  }, [projectContext]);

  return (
    <>
      <div className="prod-deploy-dashboard__menu">
        <p>
          <FormattedMessage id="prodDeployment.filterBy" />
        </p>
        <StudioSelect
          className={cn(
            'prod-deploy-dashboard__filter__select',
            filter
              ? 'create-prod-deploy__select__selected'
              : 'create-prod-deploy__select__unselected'
          )}
          placeholder="Select a filter"
          dataTestId="deployment-filter-by-deployment-type"
          options={deploymentOptions}
          disablePlaceholder
          SelectProps={{
            value: filter,
            onChange: event => {
              setFilter(event.target.value as string);
            },
          }}
        />
        <StudioSelect
          className={cn(
            'prod-deploy-dashboard__select_clear',
            filter
              ? 'create-prod-deploy__select__selected'
              : 'create-prod-deploy__select__unselected'
          )}
          placeholder={intl.formatMessage({
            id: 'prodDeployment.selectDeviceGroup',
          })}
          dataTestId="deployment-filter-by-device-group"
          options={deviceGroupsOptions}
          disablePlaceholder
          SelectProps={{
            value: deviceGroupFilter,
            onChange: e => {
              setDeviceGroupFilter(e.target.value as string);
            },
            endAdornment: <EndAdornment />,
          }}
          renderStartAdornment={renderStartAdornment}
          search
        />
      </div>
      <TableContainer
        component={Paper}
        className="prod-deploy-dashboard__deployments"
        elevation={0}
      >
        <Table stickyHeader className="prod-deploy-dashboard__table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sort === 'creationTime'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('creationTime')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.time" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'name'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('name')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.deploymentName" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'applicationName'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('applicationName')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.applicationName" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'status'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('status')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.status" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'deviceGroupName'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('deviceGroupName')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.deviceGroup" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'deviceName'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('deviceName')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.device" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.dataSource" />
              </TableCell>
              <TableCell colSpan={2}>
                <FormattedMessage id="prodDeployment.dataDrift" />
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow className="prod-deploy-dashboard__subheader">
              <TableCell colSpan={7} />
              <TableCell>
                <FormattedMessage id="prodDeployment.skew" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.confidence" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deployments.length ? (
              deployments.map(row => {
                const format = row.source?.format;
                const inputSourceType = row.source?.inputSourceType;
                const sourceId =
                  format === 'IMAGES'
                    ? sourceToTextMap[format]
                    : inputSourceType != null
                    ? sourceToTextMap[inputSourceType]
                    : 'general.na.short';
                const optimisticallyUpdatedStatus = optimisticallyUpdatedStatuses[row.id];
                const status = optimisticallyUpdatedStatus ?? row.status;

                const rowDeleted = row.status === 'DELETED';
                const rowHasError = row.status === 'ERROR';

                const deviceStatus = deviceGroups.find(
                  d => d.id === row.targetDeviceGroupId
                )?.status;

                const targetDeviceGroupName =
                  row?.targetDeviceGroupName ||
                  deviceGroups.find(d => d.id === row?.targetDeviceGroupId)?.name;

                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      'prod-deploy-dashboard__row',
                      rowHasError && 'prod-deploy-dashboard__row--error'
                    )}
                  >
                    <TableCell>
                      {row.startTime ? (
                        <HumanReadableTime date={row.startTime} />
                      ) : (
                        <p>{intl.formatMessage({id: 'prodDeployment.not.started'})}</p>
                      )}
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {row.applicationName ? row.applicationName : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {status != null && <DeployStatus status={status} />}
                    </TableCell>
                    <TableCell
                      onClick={() => {
                        if (row.targetDeviceGroupId) {
                          setDeviceGroupFilter(row.targetDeviceGroupId);
                        }
                      }}
                    >
                      {targetDeviceGroupName ? (
                        <StudioTooltip
                          title={<FormattedMessage id="prodDeployment.filterGroup" />}
                          placement="top"
                        >
                          <p className="prod-deploy-dashboard__table__device_group">
                            {deviceStatus && (
                              <DeviceStatus status={deviceStatus} showLabel={false} />
                            )}
                            {targetDeviceGroupName}
                          </p>
                        </StudioTooltip>
                      ) : (
                        <FormattedMessage id="prodDeployment.not.started.empty.field" />
                      )}
                    </TableCell>
                    <TableCell>{row.deviceName}</TableCell>
                    <TableCell>
                      <FormattedMessage id={sourceId} />
                    </TableCell>
                    <TableCell>
                      {row.dataDrift?.skewPercent != null ? (
                        <FormattedNumber
                          style="percent"
                          value={row.dataDrift.skewPercent / 100}
                        />
                      ) : (
                        <p>
                          {intl.formatMessage({
                            id: 'prodDeployment.not.started.empty.field',
                          })}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.dataDrift?.confidencePercent != null ? (
                        <FormattedNumber
                          style="percent"
                          value={row.dataDrift.confidencePercent / 100}
                        />
                      ) : (
                        <p>
                          {intl.formatMessage({
                            id: 'prodDeployment.not.started.empty.field',
                          })}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="prod-deploy-dashboard__actions">
                        {row.projectId && row.applicationId ? (
                          <DeploymentActionButton
                            inputSourceLocationType={row.source?.inputSourceLocationType}
                            inputSourceId={row.source?.inputSourceId}
                            inputSourceType={row.source?.inputSourceType}
                            streamingUrl={row.source?.streamingUrl}
                            status={status}
                            detailedTooltip={false}
                            onStart={() => handleDeployAction(row)}
                            onStop={() => handleDeployAction(row)}
                            permanentDeployment={true}
                          />
                        ) : (
                          <p className="prod-deploy-dashboard__actions__empty">
                            {intl.formatMessage({
                              id: 'prodDeployment.not.started.empty.field',
                            })}
                          </p>
                        )}
                        <StudioKebabMenu
                          items={[
                            ...(row.targetDeviceGroupId
                              ? [
                                  {
                                    title: intl.formatMessage({
                                      id: 'prodDeployment.viewGroup',
                                    }),
                                    onClick: () => {
                                      history.push(
                                        `/deployments?tab=deviceGroup&deviceGroup=${row.targetDeviceGroupId}`
                                      );
                                    },
                                  },
                                  {
                                    title: intl.formatMessage({
                                      id: 'prodDeployment.fixDeployment',
                                    }),
                                    onClick: () => {
                                      history.push(
                                        projectContext
                                          ? `/deployment/${row.id}/details?projectContext=${projectContext}`
                                          : `/deployment/${row.id}/details`
                                      );
                                    },
                                    disabled: rowDeleted || !rowHasError,
                                  },
                                ]
                              : []),
                            {
                              title: intl.formatMessage({
                                id: 'prodDeployment.viewDetails',
                              }),
                              disabled: row.status === 'DELETED',
                              onClick: () => {
                                history.push(
                                  projectContext
                                    ? `/deployment/${row.id}/details?projectContext=${projectContext}`
                                    : `/deployment/${row.id}/details`
                                );
                              },
                            },
                            {
                              title: intl.formatMessage({
                                id: 'prodDeployment.configureSPA',
                              }),
                              onClick: () => {
                                history.push(
                                  `/project/${row.projectId}/inference/${row.applicationId}/deployment/${row.id}/configure`
                                );
                              },
                              disabled:
                                !row.applicationDetails ||
                                userId !== row.applicationDetails?.ownerId ||
                                !row.applicationDetails.supportsSinglePageApplications,
                            },
                            ...(row.retrainAvailable
                              ? [
                                  {
                                    title: intl.formatMessage({
                                      id: 'prodDeployment.retrain',
                                    }),
                                    onClick: () => {
                                      dispatch(
                                        setRetrainData({
                                          projectId: row.projectId,
                                          deploymentId: row.id,
                                        })
                                      );
                                      history.push(`/project/${row.projectId}/retrain`);
                                    },
                                  },
                                ]
                              : []),
                            {
                              title: intl.formatMessage({id: 'prodDeployment.delete'}),
                              disabled: row.status === 'DELETED',
                              onClick: () =>
                                setDeleteConfirmation({isOpen: true, deployment: row}),
                            },
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                  <FormattedMessage id="dashboard.noResults" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isNumber(totalResults) && (
        <Pagination
          className="prod-deploy-dashboard__pagination"
          count={paginationCount}
          page={paginationPage}
          shape="rounded"
          onChange={(_, page) => setPage(page - 1)}
          data-testid="prod-deploy-dashboard-pagination"
          id="prod-deploy-dashboard-pagination"
        />
      )}
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'prodDeployment.deleteConfirmTitle'})}
        message={intl.formatMessage({id: 'prodDeployment.deleteConfirmMessage'})}
        submitLabel={intl.formatMessage({id: 'prodDeployment.deleteConfirmSubmitLabel'})}
        open={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({isOpen: false, deployment: null})}
        onOk={() => {
          if (deleteConfirmation.deployment) {
            handleDeleteDeployment(deleteConfirmation.deployment);
          }
          setDeleteConfirmation({isOpen: false, deployment: null});
        }}
      />
    </>
  );
};
